import React, { useEffect, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  Button,
  CardContent,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ReplayIcon from '@material-ui/icons/Replay';
import _ from 'lodash';
import { ActionTakenType } from '../../api-client/autogenerated';
import DashboardBlock from './DashboardBlock';
import { CancelButton } from '../custom-components/CustomButtons';
import {
  getActionTakenLabelsBySubscriberId,
  setActionTakenLabelsBySubscriberId,
} from '../../models/api/action-taken-labels';
import {
  ACTION_TAKEN_SECTIONS,
  canDisableActionTakenType,
  CUSTOMIZABLE_ACTION_TAKEN_TYPES,
  MAX_ACTION_TAKEN_LABEL_LENGTH,
} from '../../scripts/action-taken-labels';
import { BUILT_IN_ACTION_TAKEN_TEXT } from '../../scripts/utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableContainer: {
      marginBottom: theme.spacing(2),
    },
    // Each section is its own <table>, so by default each would size its columns to its own
    // content and the two would not line up. A fixed layout makes the shared COLUMN_WIDTHS below
    // authoritative, so every section lays out identically whatever labels it happens to hold.
    table: {
      tableLayout: 'fixed',
    },
    // Dashboard tables render their cells uppercase app-wide. Here the cells ARE the labels the
    // admin is choosing between, so they must show the exact wording that will appear in the app —
    // otherwise the default column disagrees with the field's placeholder right beside it.
    defaultLabel: {
      fontSize: 16,
      textTransform: 'none',
    },
    actionsRow: {
      marginTop: theme.spacing(1),
    },
    saveButton: {
      marginLeft: theme.spacing(1),
    },
    error: {
      color: theme.palette.error.main,
      marginTop: theme.spacing(1),
    },
  }),
);

/**
 * Column widths shared by every section's table, in header order: Enabled, Default label, Custom
 * label, Reset. One definition rather than one per table, so a column added or resized here cannot
 * line up in one section and not the other.
 */
const COLUMN_WIDTHS = ['12%', '30%', '48%', '10%'];

const ColumnWidths = () => (
  <colgroup>
    {COLUMN_WIDTHS.map((width) => (
      <col key={width} style={{ width }} />
    ))}
  </colgroup>
);

type ActionDraft = { label: string; isEnabled: boolean };
type Draft = Record<string, ActionDraft>;

const DEFAULT_DRAFT: ActionDraft = { label: '', isEnabled: true };

const buildDraft = (
  rows: { actionTaken: ActionTakenType; label?: string | null; isEnabled: boolean }[],
) => {
  const next: Draft = {};
  CUSTOMIZABLE_ACTION_TAKEN_TYPES.forEach((action) => {
    const row = rows.find((r) => r.actionTaken === action);
    next[action] = {
      label: row?.label ?? '',
      isEnabled: row ? row.isEnabled !== false : true,
    };
  });
  return next;
};

/** Trimmed copy, so trailing whitespace alone never counts as an edit. */
const normalize = (draft: Draft): Draft =>
  _.mapValues(draft, (value) => ({ ...value, label: value.label.trim() }));

type Props = {
  subscriberId: string;
};

/**
 * Lets a subscriber admin rename the SUBMITTAL review outcomes shown across their projects, and
 * switch off the ones their firm does not use.
 *
 * Renaming is display only: the stored value never changes, so filters, reports and integrations
 * keep working. Disabling is selection only: it removes the action from reviewers' dropdowns and
 * never changes how documents already carrying it are shown or processed.
 */
export default function ActionTakenLabelsPanel({ subscriberId }: Props) {
  const classes = useStyles();

  const [saved, setSaved] = useState<Draft>(() => buildDraft([]));
  const [draft, setDraft] = useState<Draft>(() => buildDraft([]));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!subscriberId) return;
    setIsLoading(true);
    getActionTakenLabelsBySubscriberId(subscriberId)
      .then((rows) => {
        const next = buildDraft(rows);
        setSaved(next);
        setDraft(next);
      })
      .catch(() => setError('Could not load action labels.'))
      .finally(() => setIsLoading(false));
  }, [subscriberId]);

  const trimmedDraft = normalize(draft);
  const isDirty = !_.isEqual(trimmedDraft, normalize(saved));

  const isTooLong = (value: string) => value.trim().length > MAX_ACTION_TAKEN_LABEL_LENGTH;
  const hasInvalidLabel = CUSTOMIZABLE_ACTION_TAKEN_TYPES.some((a) => isTooLong(draft[a].label));
  const update = (action: ActionTakenType, patch: Partial<ActionDraft>) =>
    setDraft((prev) => ({ ...prev, [action]: { ...prev[action], ...patch } }));

  const handleDiscard = () => {
    setDraft(saved);
    setError(null);
  };

  const handleSave = () => {
    setIsSaving(true);
    setError(null);
    const labels = CUSTOMIZABLE_ACTION_TAKEN_TYPES.map((action) => ({
      actionTaken: action,
      label: trimmedDraft[action].label || null,
      isEnabled: trimmedDraft[action].isEnabled,
    }));
    setActionTakenLabelsBySubscriberId(subscriberId, { labels })
      .then((rows) => {
        const next = buildDraft(rows);
        setSaved(next);
        setDraft(next);
      })
      .catch(() => setError('Could not save action labels. Please try again.'))
      .finally(() => setIsSaving(false));
  };

  return (
    <DashboardBlock title="Submittal Action Labels">
      <CardContent>
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            {ACTION_TAKEN_SECTIONS.map((section) => (
              <React.Fragment key={section.title}>
                <Typography variant="h2">{section.title}</Typography>
                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table size="small" className={classes.table}>
                    <ColumnWidths />
                    <TableHead>
                      <TableRow>
                        <TableCell>Enabled</TableCell>
                        <TableCell>Default label</TableCell>
                        <TableCell>Custom label</TableCell>
                        <TableCell align="center">Reset</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {section.actions.map((action) => {
                        const defaultLabel = BUILT_IN_ACTION_TAKEN_TEXT[action];
                        const { label, isEnabled } = draft[action];
                        const tooLong = isTooLong(label);
                        const canDisable = canDisableActionTakenType(action);
                        return (
                          <TableRow key={action}>
                            <TableCell>
                              <Tooltip
                                title={
                                  canDisable
                                    ? ''
                                    : 'Every submittal review needs an accept and a send-back outcome, so this one cannot be switched off'
                                }
                              >
                                <span>
                                  <Checkbox
                                    color="primary"
                                    checked={isEnabled}
                                    disabled={!canDisable}
                                    onChange={(e) =>
                                      update(action, { isEnabled: e.target.checked })
                                    }
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell className={classes.defaultLabel}>{defaultLabel}</TableCell>
                            <TableCell>
                              <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={label}
                                placeholder={defaultLabel}
                                error={tooLong}
                                helperText={
                                  tooLong
                                    ? `Must be ${MAX_ACTION_TAKEN_LABEL_LENGTH} characters or fewer.`
                                    : undefined
                                }
                                inputProps={{ maxLength: MAX_ACTION_TAKEN_LABEL_LENGTH }}
                                onChange={(e) => update(action, { label: e.target.value })}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Reset to default">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!label && isEnabled}
                                    onClick={() => update(action, DEFAULT_DRAFT)}
                                  >
                                    <ReplayIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </React.Fragment>
            ))}

            <Grid container justify="flex-end" alignItems="center" className={classes.actionsRow}>
              <CancelButton onClick={handleDiscard} disabled={!isDirty || isSaving}>
                Discard changes
              </CancelButton>
              <Button
                variant="contained"
                color="primary"
                className={classes.saveButton}
                disabled={!isDirty || isSaving || hasInvalidLabel}
                onClick={handleSave}
              >
                {isSaving ? <CircularProgress size={20} /> : 'Save labels'}
              </Button>
            </Grid>

            {error && (
              <Typography variant="body2" className={classes.error}>
                {error}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </DashboardBlock>
  );
}
